(() => {
	const TTL = 5 * 60 * 1000;
	const STORAGE_KEY = 'bda:collection-menus:v1';
	const requests = new Map();
	const queue = [];
	let activeRequests = 0;

	function parseFilters(html) {
		const source = new DOMParser().parseFromString(html, 'text/html');
		const filters = source.querySelectorAll('template[data-collection-filter]');
		if (!filters.length && !source.querySelector('[data-collection-filters]')) {
			throw new Error('Collection filters unavailable');
		}
		return filters;
	}

	function readCache() {
		try {
			return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
		} catch {
			return {};
		}
	}

	function saveCache(key, html, expiresAt) {
		try {
			// Bound storage and discard old themes, markets and expired catalogue data.
			const entries = Object.entries(readCache())
				.filter(([entryKey, entry]) => entryKey !== key && entry.expiresAt > Date.now())
				.slice(-15);
			entries.push([key, { html, expiresAt }]);
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
		} catch {
			// Browsing and in-page deduplication also work when storage is unavailable.
		}
	}

	function drainQueue() {
		while (activeRequests < 2 && queue.length) {
			const job = queue.shift();
			activeRequests++;
			job.run().finally(() => {
				activeRequests--;
				drainQueue();
			});
		}
	}

	function prioritize(key) {
		const index = queue.findIndex((job) => job.key === key);
		if (index > 0) queue.unshift(...queue.splice(index, 1));
	}

	function collectionFilters(url, scope, priority) {
		const key = JSON.stringify([scope, url]);
		if (priority) prioritize(key);
		const existing = requests.get(key);
		if (existing && existing.expiresAt > Date.now()) return existing.promise;
		const cached = readCache()[key];
		if (cached?.expiresAt > Date.now()) {
			try {
				const promise = Promise.resolve({ filters: parseFilters(cached.html), expiresAt: cached.expiresAt });
				requests.set(key, { promise, expiresAt: cached.expiresAt });
				return promise;
			} catch {
				// An incomplete cached response must never prevent a fresh request.
			}
		}
		let resolveRequest, rejectRequest;
		const promise = new Promise((resolve, reject) => {
			resolveRequest = resolve;
			rejectRequest = reject;
		});
		requests.set(key, { promise, expiresAt: Infinity });
		const job = { key, async run() {
			const controller = new AbortController();
			const timeout = setTimeout(() => controller.abort(), 8000);
			try {
				const response = await fetch(url, { signal: controller.signal });
				if (!response.ok) throw new Error('Collection menu unavailable');
				const html = await response.text();
				const filters = parseFilters(html);
				const expiresAt = Date.now() + TTL;
				requests.set(key, { promise, expiresAt });
				saveCache(key, html, expiresAt);
				resolveRequest({ filters, expiresAt });
			} catch (error) {
				requests.delete(key);
				rejectRequest(error);
			} finally {
				clearTimeout(timeout);
			}
		} };
		if (priority) queue.unshift(job);
		else queue.push(job);
		drainQueue();
		return promise;
	}

	class CollectionMenuFilters extends HTMLElement {
		connectedCallback() {
			this.events?.abort();
			this.events = new AbortController();
			const options = { signal: this.events.signal };
			const disclosure = this.closest('details');
			disclosure.addEventListener('toggle', () => {
				if (disclosure.open) this.load(true);
			}, options);
			disclosure.querySelector('summary').addEventListener('pointerenter', () => this.load(true), options);
			disclosure.querySelector('summary').addEventListener('focus', () => this.load(true), options);
			this.querySelector('[data-filter-retry]').addEventListener('click', () => this.load(true), options);
			// Start before interaction. Desktop/mobile instances share the same request.
			this.load(disclosure.open);
		}

		async load(priority = false) {
			if (this.readyUntil > Date.now()) return;
			if (this.loading) {
				if (priority) prioritize(JSON.stringify([this.dataset.cacheScope, this.dataset.url]));
				return this.loading;
			}
			this.dataset.state = 'loading';
			this.setAttribute('aria-busy', 'true');
			const status = this.querySelector('[role="status"]');
			const error = this.querySelector('[data-filter-error]');
			status.hidden = Boolean(this.readyUntil);
			error.hidden = true;
			this.loading = collectionFilters(this.dataset.url, this.dataset.cacheScope, priority)
				.then(({ filters, expiresAt }) => {
					if (!this.isConnected) return;
					this.render(filters);
					this.readyUntil = expiresAt;
					this.dataset.state = 'ready';
				})
				.catch(() => {
					this.dataset.state = 'error';
					error.hidden = false;
				})
				.finally(() => {
					status.hidden = true;
					this.loading = null;
					this.setAttribute('aria-busy', 'false');
				});
			return this.loading;
		}

		render(filters) {
			const showAll = this.dataset.layout === 'brands';
			const featuredBrands = (this.dataset.featuredBrands || '').split(',').filter(Boolean);
			const materials = (this.dataset.materials || '').split(',').filter(Boolean);
			const alphabetic = (a, b) => a.textContent.trim().localeCompare(b.textContent.trim(), document.documentElement.lang, { sensitivity: 'base' });
			this.querySelectorAll('[data-filter-slot]').forEach((column) => {
				const source = Array.from(filters).find((filter) => filter.dataset.collectionFilter === column.dataset.filterSlot);
				const list = source?.content.querySelector('ul')?.cloneNode(true);
				if (!list?.children.length) {
					column.hidden = true;
					return;
				}
				column.hidden = false;
				const values = Array.from(list.children);
				const isBrand = column.dataset.filterSlot === this.dataset.brandFilter;
				const isMaterial = column.dataset.filterSlot === this.dataset.materialFilter;
				if (isBrand) {
					values.sort(alphabetic);
				} else if (isMaterial && materials.length) {
					values.sort((a, b) => materials.indexOf(a.dataset.valueKey) - materials.indexOf(b.dataset.valueKey));
				} else if (!showAll) {
					values.sort((a, b) => Number(b.dataset.productCount) - Number(a.dataset.productCount) || a.textContent.localeCompare(b.textContent, document.documentElement.lang));
				}
				const featured = showAll && isBrand
					? featuredBrands.map((key) => values.find((value) => value.dataset.valueKey === key)).filter(Boolean)
					: [];
				const remaining = values.filter((value) => !featured.includes(value));
				list.replaceChildren(...(showAll ? remaining : remaining.slice(0, 12)));
				const container = column.querySelector('[data-filter-values]');
				container.replaceChildren();
				if (featured.length) {
					const featuredList = list.cloneNode(false);
					featuredList.classList.add('header-menu-columns__links--featured');
					featuredList.append(...featured);
					container.append(featuredList);
				}
				container.append(list);
				column.querySelector('.header-collection-menu__more').hidden = showAll || values.length <= 12;
			});
		}

		disconnectedCallback() {
			this.events.abort();
		}
	}

	if (!customElements.get('collection-menu-filters')) {
		customElements.define('collection-menu-filters', CollectionMenuFilters);
	}
})();
